import {
  Enum_Patientvisit_Status,
  PatientVisitInput,
  getChecksum,
  useUpdatePatientVisitMutation,
} from '@cyren/common-lib';
import produce from 'immer';
import { difference, first, intersection, isEmpty, last, size, union } from 'lodash';
import { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { atom, useRecoilState, useRecoilValue } from 'recoil';
import { useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import { AppStateCookie } from '../../app/use-app-state';
import { PaDataRepoState, useData } from '../../report/use-data';
import { onError } from '../../utils/apollo-utils';
import { getVisitFormDataFromSystemValueMap } from '../../utils/models/question';
import { NavParamPaSurvey, PaSurveyStateType, ReportStateType } from '../patient-types';
import { PaErrorState, PaReportState } from './use-report';
import { RecordErrorHandler } from '../pa-prop-types';
import { useTreeHelpers } from './use-tree-helpers';
import ContentError from '../../common/content-error/content-error';
import { AthenaServices } from '../../lib/athena/athena-services';

export const PaSurveyState = atom<PaSurveyStateType>({
  key: 'PaSurveyState',
  default: {
    adaptedTreeKeys: [],
  },
});

// aid를 global state로 관리
export const AidState = atom<string | null>({
  key: 'AidState',
  default: null,
});

export function usePaSurveyParams() {
  return useParams<NavParamPaSurvey>();
}

export function useSurveyState() {
  const params = usePaSurveyParams();
  const { orgId, shortId, treeKey } = params;

  const nav = useNavigate();
  const location = useLocation();
  const [, { getTreeByKey }] = useData();
  const currentAid = useRecoilValue(AidState);
  
  // URL에서 aid 파라미터 가져오기
  const searchParams = new URLSearchParams(location.search);
  const urlAid = searchParams.get('aid');


  const [{ visit }] = useRecoilState(PaDataRepoState);
  const [surveyState, setSurveyState] = useRecoilState(PaSurveyState);
  const [reportState, setReportState] = useRecoilState(PaReportState);
  const [errorState, setErrorState] = useRecoilState(PaErrorState);
  const [{ survey }] = useRecoilState(PaDataRepoState);
  const { sltStr } = useStaticLocales();

  const [{ localeKey }] = useRecoilState(AppStateCookie);

  const [, { isTreeVisible }] = useTreeHelpers({ treeState: first(reportState.treeStates) });

  useEffect(() => {
    if (reportState.locale !== localeKey) {
      setReportState((st) =>
        produce(st, (dr) => {
          dr.locale = localeKey;
        }),
      );
    }
  }, [localeKey]);

  const surveyId = survey?.id;

  const baseUrl = `/s/${orgId}/${shortId}`;

  const { questionTreeKeys } = survey || {};
  const { curTreeKey } = surveyState;

  const curTreeIdx = questionTreeKeys?.findIndex((k) => k === curTreeKey);
  const isLastTree = !isEmpty(questionTreeKeys) && last(questionTreeKeys) === curTreeKey;

  function setCurTreeKey(nTreeKey: string) {
    const nState = produce(surveyState, (draft) => {
      draft.curTreeKey = nTreeKey;
    });

    setSurveyState(nState);
  }

  function getStageProgress(nState: ReportStateType, transitioningToTreeKey: string | null | undefined, userCompleted: boolean) {

    const completedStages: string[] = [];
    let activeStage: string | null = null;

    // User will not necessarily see all treeKeys (some may be disabled or hidden from user),
    // but they can also move forwards and then move backwards.  Create a list of all possible
    // top-level tree keys that they have actually seen, which will give us a sense of their
    // top level progress
    const seenTreeKeys = union(nState.treeStates.map(i => i.treeKey),
      transitioningToTreeKey ? [transitioningToTreeKey] : []);
    const seenTrees = intersection(survey?.questionTreeKeys, seenTreeKeys).map(i => getTreeByKey(i));


    seenTrees?.forEach(tree => {
      if (tree?.stageBoundary) {
        if (activeStage) {
          completedStages.push(activeStage);
        }
        activeStage = tree.treeKey;
      }
    });

    if (userCompleted && activeStage) {
      completedStages.push(activeStage);
      activeStage = null;
    }

    const allPossibleStages = survey?.questionTreeKeys?.
      map(aTreeKey => getTreeByKey(aTreeKey)).
      filter(tree => tree?.stageBoundary).
      map(tree => tree?.treeKey);

    const unreachedStages = difference(allPossibleStages,
      [...completedStages, activeStage]);

    const result = {
      completedStages,
      activeStage,
      unreachedStages
    }
    // console.log('stage progress', result);
    return result;
  }

  const [updatePatientVisit] = useUpdatePatientVisitMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      if (err.message.includes('ERROR-011')) {
        toast.error(sltStr({ key: 'msg-cannot-go-back' }), {
          toastId: 'msg-cannot-go-back',
        });
        nav(`${baseUrl}/finished`);
        return;
      }
      onError(err);
    },
    onCompleted: (data) => {
      // 실시간으로 Athena 통합 호출
      if (data?.updatePatientVisit?.data?.id) {
        // Athena 통합 로직 실행
        triggerAthenaIntegration(data.updatePatientVisit.data.id);
      }
    },
  });

  const deployed = survey?.deployment?.id != null;

  // Athena 통합 함수
  const triggerAthenaIntegration = async (visitId: string) => {
    try {
      // visit 데이터에서 필요한 정보 추출
      console.log('Triggering Athena integration for visit:', visitId);
      
      // visit 데이터가 있는 경우에만 실행
      if (visit?.id) {
        // 1. Patient Check-in (환자가 설문을 시작할 때)
        if (visit.status === Enum_Patientvisit_Status.Reporting) {
          // appointmentId는 visit의 uuid나 id를 사용할 수 있음
          const appointmentId = visit.uuid || visit.id.toString();
          console.log('Patient starting survey - check-in:', appointmentId);
          // await AthenaServices.handlePatientCheckIn(appointmentId);
        }
        
        // 2. HPI 업데이트 (설문 데이터가 업데이트될 때마다)
        if (reportState?.systemValueMap) {
          const hpiData = generateHPIFromSurveyData(reportState.systemValueMap);
          if (hpiData) {
            const encounterId = visit.uuid || visit.id.toString();
            console.log('Updating HPI with survey data:', hpiData);
            // await AthenaServices.updateHPI(encounterId, hpiData);
          }
        }
        
        // 3. 설문 완료 시 (status가 reported로 변경될 때)
        if (visit.status === Enum_Patientvisit_Status.Reported) {
          console.log('Survey completed - final Athena sync');
          // 최종 Athena 동기화
          // await AthenaServices.finalizePatientVisit(visit.id.toString());
        }
      }
      
    } catch (error) {
      console.error('Athena integration failed:', error);
    }
  };

  // 설문 데이터에서 HPI 생성
  const generateHPIFromSurveyData = (systemValueMap: any) => {
    if (!systemValueMap) return null;
    
    // 설문 데이터를 HPI 형식으로 변환
    const hpiData = {
      chiefComplaint: systemValueMap.chiefComplaint || '',
      symptoms: systemValueMap.symptoms || '',
      medicalHistory: systemValueMap.medicalHistory || '',
      medications: systemValueMap.medications || '',
      allergies: systemValueMap.allergies || '',
      // 기타 필요한 필드들...
    };
    
    return hpiData;
  };

  const updateVisit = (
    nState: ReportStateType,
    { data }: { data?: PatientVisitInput } | undefined = {},
    transitioningToTreeKey: string | null = null
  ) => {
    if (visit?.id != null) {
      const visitFormData = getVisitFormDataFromSystemValueMap({
        systemValueMap: nState?.systemValueMap,
      });
      
      const aid = searchParams.get('aid');

      // This is useful debugging code that can show the specific user answer fields and
      // values being written to the server.  Leaving the code in place so that it can
      // be uncommented for debugging
      /*
      console.log('writing report state to server:', reportState);
      let result = '';
      nState.treeStates.forEach((i) => {
        if (i && i.answerMap && i.answerMap) {
          // eslint-disable-next-line
          for (const [key, value] of Object.entries(i.answerMap)) {
            let displayValue = null;
            if (value && value?.answerKeys) {
              displayValue = `${value.answerKeys[0]}`
            }
            if (value && value.answerValues) {
              displayValue += `:${value.answerValues[0]}`
            }
            result += `send=> ${key}: ${displayValue}\n`;
          }
        }
      });
      console.log(result);
      */

      const userCompleted = data?.status === Enum_Patientvisit_Status.Reported;

      updatePatientVisit({
        variables: {
          id: visit?.id,
          data: {
            ...data,
            ...visitFormData,
            deployed,
            organization: survey?.organization?.id,
            enumSurveyType: survey?.type,
            surveyState: {
              reportState: {
                ...nState,
                errorState
              },
              surveyState: {
                ...surveyState,
                ...getStageProgress(nState, transitioningToTreeKey, userCompleted)
              },
              checksum: getChecksum(aid ? { ...nState, aid } : nState),
            },
            dob: isEmpty(data?.dob) ? null : data?.dob,
          },
        },
      });
    }
  };

  function startSurvey() {
    const firstTreeKey = first(questionTreeKeys);
    nav(`${baseUrl}/${firstTreeKey}`);

    if (visit?.id) {
      updateVisit(reportState, {
        data: {
          deployed,
          organization: survey?.organization?.id,
          enumSurveyType: survey?.type,
          visitedAt: new Date(),
          status: Enum_Patientvisit_Status.Reporting,
        },
      }, firstTreeKey);
    }
  }

  function onSave() {
    updateVisit(reportState, { data: { status: Enum_Patientvisit_Status.Reported } });
    nav(`/s/${orgId}/${shortId}/finished`);
  }

  function goNextTree() {
    if (curTreeIdx != null && curTreeIdx >= 0) {
      let nextTreeKeyIdx = curTreeIdx;
      let destinationTreeKey = null;

      while (!destinationTreeKey && survey && nextTreeKeyIdx < (survey.questionTreeKeys.length - 1)) {
        nextTreeKeyIdx++;
        const nextTreeKey = survey?.questionTreeKeys[nextTreeKeyIdx];
        if (nextTreeKey && isTreeVisible({ treeKey: nextTreeKey })) {
          destinationTreeKey = nextTreeKey;
        }
      }

      // none of the remaining trees are accessible, so switch over to a save operation
      if (!destinationTreeKey) {
        throw new ContentError(ContentError.NO_NEXT_TREE);
      }

      updateVisit(reportState, {}, destinationTreeKey);

      nav(`${baseUrl}/${destinationTreeKey}`);
    }
  }

  const recordUnhandledError: RecordErrorHandler = ({ message }) => {
    if (errorState?.maxErrorsReached) {
      // don't flood the system with error message
      return;
    }

    // don't put more than 20 errors into the queue
    const atMaxErrors = (size(errorState.unhandledErrors) + 1) >= 20
    setErrorState({
      ...errorState,
      maxErrorsReached: atMaxErrors,
      unhandledErrors: [...errorState.unhandledErrors,
      {
        createdOn: new Date(Date.now()).toISOString(),
        message: message.message,
        source: message.source,
        lineInfo: `${message.lineno}:${message.colno}`
      }
      ]
    });
  };

  function onInterimSave() {
    if (curTreeIdx != null && curTreeIdx >= 0) {
      if (reportState.systemValueMap?.dob === '') {
        // don't allow an interim report that has a blank dob to be written to the server, since this
        // will generate a graphql error regarding an invalid state
        // eslint-disable-next-line
        console.log('Invalid dob, ignoring interim save request');
        return;
      }
      updateVisit(reportState);
    }
  }

  // init
  // useEffect(() => {
  //   if (isEmpty(flowState.survey) && flowKey) {
  //     const nFlow = getFlowByKey(flowKey);

  //     if (nFlow) {
  //       const nState = produce(flowState, (draft) => {
  //         draft.survey = nFlow;
  //         draft.adaptedTreeKeys = nFlow.treeKeys;
  //       });

  //       setFlowState(nState);
  //     }
  //   }
  // }, [flowKey]);

  // aid 변경 감지 및 상태 초기화
  useEffect(() => {
    if (urlAid && currentAid && urlAid !== currentAid) {
      console.log('useSurveyState - aid 변경 감지:', { urlAid, currentAid });
      
      // localStorage에서 이전 aid 가져오기
      const previousAid = localStorage.getItem('currentAid');
      
      if (urlAid !== previousAid) {
        console.log('aid 변경으로 인한 상태 초기화');
        
        // 설문 상태 초기화
        setSurveyState({
          adaptedTreeKeys: [],
          curTreeKey: undefined,
          checkFlag: undefined,
          patientDetails: undefined,
          encounterId: undefined,
          encounterCheckStatus: undefined,
          encounterCheckReason: undefined,
        });
        
        // 리포트 상태 초기화
        setReportState({
          globalAnswerMap: {},
          treeStates: [],
          nextTreeKeys: [],
          presentIllness: [],
          ccKey: undefined,
          descriptiveReport: undefined,
        });
      }
    }
  }, [urlAid, currentAid, setSurveyState, setReportState]);

  // update curTreeKey on navigate (or back button)
  useEffect(() => {
    if (survey && treeKey && curTreeKey !== treeKey) {
      setCurTreeKey(treeKey);
    }
  }, [survey, treeKey, curTreeKey]);

  return [
    { surveyId, reportState, surveyState, curTreeIdx, isLastTree, params, patientDetails: surveyState.patientDetails },
    {
      updateVisit,
      setReportState,
      navigate: nav,
      startSurvey,
      goNextTree,
      onSave,
      onInterimSave,
      setCurTreeKey,
      setSurveyState,
      recordUnhandledError
    },
  ] as const;
}
